/**
 * Entity-adapter slice factory for the createAsyncThunk+normalizr lineage.
 * Warehouse-only, sunset-track: every current caller lives under
 * `features/warehouse/` and passes `parentPath: 'warehouse'`. Do not add
 * callers outside warehouse or generalize it further — new list-state needs
 * go through `createViewReducers` (utils/viewState.js) directly, and new
 * entity resources go through RTK Query (`features/entities/`).
 *
 * Contains:
 * - createEntitySlice — builds a slice + selectors for one adapter-backed
 *   entity, wired to the 5-thunk load-many/load-one/create/edit/remove
 *   contract
 */
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';

import { createViewReducers, initialViewState } from '@sophocles/utils/viewState';

export const createEntitySlice = (
  name,
  schema,
  loadEntities,
  loadEntity,
  createEntity,
  editEntity,
  removeEntity,
  parentPath = null,
) => {
  const entityAdapter = createEntityAdapter();
  const slice = createSlice({
    name,
    initialState: entityAdapter.getInitialState({
      view: initialViewState,
    }),
    reducers: {
      ...createViewReducers(),
    },
    extraReducers: builder => {
      builder
        .addCase(loadEntities.fulfilled, (state, { payload }) => {
          if (payload) {
            if (payload.length > 0) {
              const { entities } = normalize(payload, [schema]);
              entityAdapter.upsertMany(state, Object.values(entities[name]));
              state.view.lastLoaded = new Date().toISOString();
            }
          }
        })
        .addCase(loadEntity.fulfilled, (state, action) => {
          if (action.payload) {
            if (action.payload.result && action.payload.entities) {
              const { entities, result } = action.payload;
              entityAdapter.upsertOne(state, entities[name][result]);
            } else {
              entityAdapter.upsertOne(state, action.payload);
            }
          }
        })
        .addCase(createEntity.fulfilled, (state, action) => {
          if (action.payload) {
            if (action.payload.result && action.payload.entities) {
              const { entities, result } = action.payload;
              entityAdapter.upsertOne(state, entities[name][result]);
            } else {
              entityAdapter.upsertOne(state, action.payload);
            }
          }
        })
        .addCase(editEntity.fulfilled, (state, action) => {
          if (action.payload) {
            if (action.payload.result && action.payload.entities) {
              const { entities, result } = action.payload;
              entityAdapter.upsertOne(state, entities[name][result]);
            } else {
              entityAdapter.upsertOne(state, action.payload);
            }
          }
        })
        .addCase(removeEntity.fulfilled, (state, action) => {
          entityAdapter.removeOne(state, action.payload);
        });
    },
  });

  let selectEntityEntities;
  if (parentPath) {
    selectEntityEntities = state => {
      return state[parentPath][name];
    };
  } else {
    selectEntityEntities = state => state[name];
  }

  const { selectById: selectEntityById, selectAll: selectAllEntities } =
    entityAdapter.getSelectors(selectEntityEntities);

  const selectView = state => state[parentPath][name]?.view;
  const selectPage = state => selectView(state)?.page;
  const selectPageSize = state => selectView(state)?.pageSize;
  const selectSearch = state => selectView(state)?.search;
  const selectStart = state => selectView(state)?.start;
  const selectEnd = state => selectView(state)?.end;
  const selectFilters = state => selectView(state)?.filters;
  const selectLastLoaded = state => selectView(state)?.lastLoaded;
  const selectIsLoading = state => selectView(state)?.loading;

  return {
    reducer: slice.reducer,
    actions: {
      ...slice.actions,
    },
    selectors: {
      selectEntityById,
      selectAllEntities,
      selectPage,
      selectPageSize,
      selectSearch,
      selectStart,
      selectEnd,
      selectFilters,
      selectLastLoaded,
      selectIsLoading,
    },
  };
};
